import { Component, Element, h, Host, Prop, Event, EventEmitter } from '@stencil/core';
import { hasSlot } from '../../utils';
import { MenuItem } from './menu-item.interface';

@Component({
  tag: 'o2-menu-item',
  styleUrl: 'menu-item.scss',
  shadow: true,
})
export class O2MenuItem {
  private hasIconSlot: boolean;
  private hasTextSlot: boolean;

  @Element() el: HTMLO2MenuItemElement;

  @Prop({ reflect: true }) value: string;

  @Prop({ reflect: true }) name?: string;

  @Prop({ reflect: true, mutable: true }) text?: string;

  @Prop({ reflect: true, mutable: true }) selected?: boolean = false;

  @Prop({ reflect: true, mutable: true }) active?: boolean = false;

  @Prop({ reflect: true, mutable: true }) disabled?: boolean = false;

  @Prop() multiselect?: boolean = false;

  /**
   * Emitted when a menu item is selected.
   */
  @Event({ bubbles: true }) o2MenuItemSelected!: EventEmitter<MenuItem>;

  /**
   * Emitted when a menu item is clicked.
   */
  @Event({ bubbles: true }) o2MenuItemClicked!: EventEmitter<MenuItem>;

  // set selected state on click
  private onMenuItemClick = () => {
    if (!this.disabled) {
      this.selected = !this.selected;

      const menuItem: MenuItem = {
        value: this.value,
        text: this.text,
        selected: this.selected,
        disabled: this.disabled,
      };

      this.o2MenuItemClicked.emit(menuItem);
      if (this.selected) {
        this.o2MenuItemSelected.emit(menuItem);
      }
    }
  }

  private setTabIndexOnFirstMenuItem = () => {
    if (this.el.previousElementSibling === null) {
      this.el.setAttribute('tabindex', '0');
    }
  }

  connectedCallback() {
    this.hasIconSlot = hasSlot(this.el, '[slot="icon"]');
    this.hasTextSlot = hasSlot(this.el, '[slot="text"]') || !!this.text;

    this.setTabIndexOnFirstMenuItem();

    if (!this.text) {
      this.text = this.el.textContent.trim();
    }
  }

  render() {
    const hasContent = !this.hasIconSlot && !this.hasTextSlot;

    return (
      <Host role="menuitem" aria-disabled={this.disabled ? 'true' : 'false'}>
        <div
          class={`menu__item ${this.selected ? 'menu__item--selected' : ''} ${this.active ? 'menu__item--active' : ''} ${this.disabled ? 'menu__item--disabled' : ''}`}
          role="menuitem"
          onClick={this.onMenuItemClick}
        >
          {this.hasIconSlot && (
            <div class="menu__icon">
              <slot name="icon"></slot>
            </div>
          )}

          {this.hasTextSlot && <div class="menu__text">{this.text ? this.text : <slot name="text"></slot>}</div>}

          {this.multiselect && <o2-checkbox name={this.name} value={this.value} checked={this.selected} tabindex="-1"></o2-checkbox>}

          {hasContent && (
            <div class="menu__content">
              <slot></slot>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
